/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Card, TextField, IconButton, Grid, CardContent, makeStyles } from "@material-ui/core";
import { CopyButton } from "../../agentConsole/components/Common/CopyButton";

import Autocomplete from "@material-ui/lab/Autocomplete";
import {
	getLexBotV2Details,
	getLexBotV2Locales,
	associateLexBotV2ToConnect,
	disassociateLexBotV2ToConnect,
	getLexBotV2Aliases,
} from "../services/LexBotV2Service";
import { IntentsAndSlotTypeSection } from "../components/IntentsAndSlotTypeSection";
import CheckIcon from "@material-ui/icons/Check";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import { ActionButtons, CopyIconButton, ProgressOverlay, ShowSnackbar } from "../../../components/Common";

const COMPONENT_NAME = "Admin lexBotManager/components/LexBotDisplayCard";

const DisplayLabel = ({ labelName, labelText }) => {
	return (
		<>
			<label style={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>{labelName}</label>
			<div style={{ wordBreak: "break-word" }}>{labelText || ""}</div>
		</>
	);
};

const useStyles = makeStyles({
	saveBtn: {
		backgroundColor: (props) => (props.alias?.associated ? "#cc1f20" : "green"),
		"&:hover": {
			backgroundColor: (props) => (props.alias?.associated ? "#cc1f20" : "green"),
		},
		"&:focus": {
			backgroundColor: (props) => (props.alias?.associated ? "#cc1f20" : "green"),
		},
		"&:disabled": {
			backgroundColor: (props) => (props.alias?.associated ? "#cc1f20" : "green"),
			color: "#fff",
		},
	},
});

const CustomActionButton = (props) => {
	const { setRefresh, setToggleAssociate, alias } = props;
	const classes = useStyles(props);
	return (
		<ActionButtons
			showCancel
			showSave
			className="flex-wrap"
			cancelBtnClass="my-2 my-lg-0"
			saveBtnClass={`${classes.saveBtn} my-2 my-lg-0`}
			onCancel={() => setRefresh(true)}
			onSave={() => setToggleAssociate(true)}
			cancelLabel="Refresh"
			saveLabel={alias?.associated ? "Disassociate From Connect" : "Associate To Connect"}
		/>
	);
};

/**
 *This component is used to load and render the currently selected lex bot details to a display card.
 *
 * @param {Object} originalLexBot The lex bot passed into the display card from the page. This is the lex bot information loaded from the list api call.
 * @param {(Boolean) => void} setBotDetailsLoading This set the value true/false while details is loading.
 * @param {(String) => void} selectedBotId This is a lex bot index.
 * @param {(Object) => void} updateLocalLexBot This is a callback method to update the local lex bot with the newly loaded/updated lex bot information.
 */
export const LexBotV2DisplayCard = ({
	originalLexBot,
	setBotDetailsLoading,
	selectedBotId,
	updateLocalLexBot,
	associatedAliases,
	updateAliases,
	botDetailsLoading,
}) => {
	const [botId, setBotId] = React.useState(selectedBotId);
	const [botLoading, setBotLoading] = React.useState(false);
	const [detailsLoaded, setDetailsLoaded] = React.useState(false);
	const [toggleAssociate, setToggleAssociate] = React.useState(false);
	const [associating, setAssociating] = React.useState(false);
	const [lexBotLocales, setLexBotLocales] = React.useState([]);
	const [lexBotAlias, setLexBotAlias] = React.useState([]);
	const [alias, setAlias] = React.useState(undefined);
	const [lexBot, setLexBot] = React.useState(originalLexBot || []);
	const [notification, setNotification] = React.useState({ message: "", severity: "success" });
	const [refresh, setRefresh] = React.useState(false);
	const [onAliasChange, setOnAliasChange] = React.useState(false);

	const handleSnackBarClose = () => {
		setNotification((cur) => ({ ...cur, message: "" }));
	};
	const handleBotLoading = React.useCallback(
		(loading) => {
			setBotLoading(loading);
			setBotDetailsLoading(loading);
		},
		[setBotDetailsLoading],
	);
	React.useEffect(() => {
		if (botId !== selectedBotId) {
			if (!originalLexBot) {
				setLexBot(originalLexBot);
				setDetailsLoaded(true);
				handleBotLoading(false);
			} else {
				setLexBotLocales([]);
				setAlias(undefined);
				setLexBot(undefined);
				setDetailsLoaded(false);
				handleBotLoading(false);
			}
			setBotId(selectedBotId);
		}
	}, [handleBotLoading, originalLexBot, botId, selectedBotId]);

	React.useEffect(() => {
		const toggleAssociateLexBot = async () => {
			try {
				let result;
				let associated = [];
				if (alias.associated) {
					result = await disassociateLexBotV2ToConnect(lexBot.botId, alias.botAliasId);
					associated = associatedAliases?.filter((associate) => associate.aliasId !== alias.botAliasId);
				} else {
					result = await associateLexBotV2ToConnect(lexBot.botId, alias.botAliasId);
					associated = [...associatedAliases, { botId: originalLexBot.botId, aliasId: alias.botAliasId }];
				}
				if (result) {
					let newLexBot = { ...lexBot };
					let newAlias = { ...alias };
					newLexBot.associated = associated?.find((x) => x.botId === originalLexBot.botId) ? true : false;
					newAlias.associated = alias.associated ? false : true;
					const lexBotAliasCopy = [...lexBotAlias];
					const targetIndex = lexBotAlias.findIndex((f) => f.botAliasId === alias.botAliasId);
					lexBotAliasCopy[targetIndex] = newAlias;
					setLexBotAlias(lexBotAliasCopy);
					updateLocalLexBot(newLexBot);
					updateAliases(associated);
					setAlias(newAlias);
					setLexBot(newLexBot);
					setNotification({
						message: "Successfully saved Lex bot.",
						severity: "success",
					});
				}
			} catch (error) {
				setNotification({
					message: "Failed to save Lex bot.",
					severity: "error",
				});
				v2.error(`Error associating lex bot to connect instance`, {
					additionalData: {
						error: errorToObject(error),
						data: { lexBot, region: process.env.REACT_APP_AWS_REGION },
					},
					logCode: errorCodes.ASSOCIATE_LEX_BOT_V2_TO_CONNECT_API_ERROR,
					componentName: COMPONENT_NAME,
				});
			} finally {
				setToggleAssociate(false);
				setAssociating(false);
			}
		};
		if (toggleAssociate && !associating) {
			setAssociating(true);
			toggleAssociateLexBot();
		}
	}, [
		toggleAssociate,
		associating,
		lexBot,
		alias,
		updateLocalLexBot,
		associatedAliases,
		originalLexBot,
		updateAliases,
		lexBotAlias,
	]);

	React.useEffect(() => {
		if (onAliasChange) {
			setOnAliasChange(false);
			setLexBotLocales([]);
			setDetailsLoaded(false);
		}
		if (refresh) {
			setRefresh(false);
			setLexBot(undefined);
			setDetailsLoaded(false);
			setLexBotLocales([]);
			setAlias(undefined);
		}
	}, [refresh, onAliasChange]);
	React.useEffect(() => {
		const getLexBotDetails = async () => {
			try {
				let promises = [getLexBotV2Aliases(originalLexBot.botId), getLexBotV2Details(originalLexBot.botId)];
				promises = await Promise.all(promises);
				const aliasesList = promises[0];
				const lexBotDetails = promises[1];
				if (associatedAliases && associatedAliases.length > 0) {
					for (let x = 0; x < aliasesList.length; x++) {
						if (
							associatedAliases?.find(
								(bot) => bot.aliasId === aliasesList[x].botAliasId && bot.botId === originalLexBot.botId,
							)
						) {
							aliasesList[x] = { ...aliasesList[x], associated: true };
						}
					}
				}
				if (!alias) {
					if (aliasesList[0].botVersion) {
						let lexBotLocale = await getLexBotV2Locales(originalLexBot.botId, aliasesList[0].botVersion);
						setLexBotLocales(lexBotLocale);
					}
					setAlias(aliasesList[0]);
				} else {
					if (alias.botVersion) {
						let lexBotLocale = await getLexBotV2Locales(originalLexBot.botId, alias.botVersion);
						setLexBotLocales(lexBotLocale);
					}
				}
				updateLocalLexBot({ ...lexBotDetails, associated: originalLexBot.associated });
				setLexBotAlias(aliasesList);
				setLexBot({ ...lexBotDetails, associated: originalLexBot.associated });
				setDetailsLoaded(true);
				handleBotLoading(false);
			} catch (error) {
				v2.error(`Error loading lex bot details`, {
					additionalData: {
						error: errorToObject(error),
						data: { originalLexBot },
					},
					logCode: errorCodes.GET_LEX_BOT_V2_DETAILS_API_ERROR,
					componentName: COMPONENT_NAME,
				});
			}
		};
		if (!botLoading && !detailsLoaded && originalLexBot !== undefined) {
			handleBotLoading(true);
			getLexBotDetails();
		} else if (!detailsLoaded) {
			setDetailsLoaded(true);
		}
	}, [
		botLoading,
		handleBotLoading,
		detailsLoaded,
		originalLexBot,
		alias,
		updateLocalLexBot,
		lexBotAlias,
		associatedAliases,
	]);

	return (
		<Card style={{ width: "100%", minHeight: "100%" }}>
			<ShowSnackbar message={notification.message} severity={notification.severity} onClose={handleSnackBarClose} />
			{!botLoading && detailsLoaded && originalLexBot && botId === selectedBotId ? (
				<CardContent>
					<Grid container justifyContent="space-between">
						<div>
							<div className="d-flex justify-content-between flex-wrap">
								<div className="d-flex">
									<Autocomplete
										value={alias}
										onChange={(_event, newValue) => {
											if (newValue) {
												setAlias(newValue);
												setOnAliasChange(true);
											}
										}}
										disabled={botDetailsLoading}
										getOptionLabel={(option) => option.botAliasName}
										renderOption={(props) => {
											return (
												<div className="d-flex align-items-center justify-content-between w-100">
													<span>{props.botAliasName}</span>
													<span>{props.associated ? <CheckIcon /> : ""}</span>
												</div>
											);
										}}
										getOptionSelected={(option, value) => option.botAliasName === value.botAliasName}
										options={lexBotAlias}
										style={{ width: 200 }}
										renderInput={(params) => <TextField {...params} label={<b style={{ color: "gray" }}>Aliases</b>} />}
									/>
									<IconButton size="small" style={{ marginTop: "18px", marginLeft: "5px" }}>
										<CopyButton
											value={originalLexBot && originalLexBot.botName}
											style={{ width: "25px", color: "#707070" }}
										/>
									</IconButton>
								</div>
								<span style={{ marginLeft: "5px" }}>
									<label style={{ fontSize: "12px", color: "gray", fontWeight: 600 }}>Version</label>
									<div>{(alias && alias.botVersion) || "Not assigned"}</div>
								</span>
								<span style={{ marginLeft: "15px" }}>
									<label style={{ fontSize: "12px", color: "gray", fontWeight: 600 }}>Lex Bot Status</label>
									<div>
										{alias && alias.botAliasStatus === "Available" ? (
											<label style={{ color: "green" }}>{(alias && alias.botAliasStatus) || ""}</label>
										) : (
											<label style={{ color: "red" }}>{(alias && alias.botAliasStatus) || ""}</label>
										)}
									</div>
								</span>
							</div>
							<div style={{ fontSize: 12 }}>
								<label style={{ fontSize: "12px", color: "gray", fontWeight: 600 }}>Last Updated:</label>{" "}
								{alias && new Date(alias.lastUpdatedDateTime).toLocaleString()}
							</div>
						</div>
						<CustomActionButton setRefresh={setRefresh} setToggleAssociate={setToggleAssociate} alias={alias} />
						{associating && <ProgressOverlay />}
					</Grid>
					<Grid item>
						<h6
							style={{
								marginTop: "25px",
								background: "#eeeeee",
								borderRadius: "5px",
								paddingLeft: "15px",
								width: "100%",
								padding: "5px",
								fontWeight: 600,
								fontSize: "18px",
							}}
						>
							Bot Details
						</h6>
						<div style={{ display: "flex" }}>
							<div style={{ margin: "20px", minWidth: "40%" }}>
								<DisplayLabel
									labelName="Bot Name"
									labelText={
										<Grid container alignItems="center">
											<div>{originalLexBot && originalLexBot.botName}</div>
											<CopyIconButton value={originalLexBot?.botName} />
										</Grid>
									}
								/>
							</div>
							<div style={{ margin: "20px", minWidth: "40%" }}>
								<DisplayLabel labelName="Bot Description" labelText={originalLexBot && originalLexBot.description} />
							</div>
						</div>
						<div style={{ display: "flex" }}>
							<div style={{ margin: "20px", minWidth: "40%" }}>
								<DisplayLabel
									labelName="Idle Session Time To Live (Minutes)"
									labelText={originalLexBot && originalLexBot.idleSessionTTLInSeconds / 60}
								/>
							</div>
							<div style={{ margin: "20px", minWidth: "40%" }}>
								<DisplayLabel
									labelName="COPPA Compliant"
									labelText={originalLexBot?.dataPrivacy?.childDirected ? "Yes" : "No"}
								/>
							</div>
						</div>
						{lexBotLocales &&
							lexBotLocales.map((locale) => (
								<IntentsAndSlotTypeSection
									key={locale.localeId}
									botId={originalLexBot.botId}
									botName={originalLexBot.botName}
									botAliasName={alias?.botAliasName}
									botVersion={alias?.botVersion}
									locale={locale}
									setBotDetailsLoading={setBotDetailsLoading}
								/>
							))}
						{lexBotLocales && lexBotLocales.length === 0 && (
							<div style={{ color: "gray", textAlign: "center" }}>No languages associated with alias.</div>
						)}
					</Grid>
				</CardContent>
			) : originalLexBot === undefined ? (
				<div style={{ color: "gray", marginTop: "30%", textAlign: "center" }}>
					To get started, select a lex bot from the list on the left.
				</div>
			) : (
				<div>
					<i
						style={{ left: "50%", position: "relative", top: "300px" }}
						className="fas fa-spinner loading-spinner spin-animation"
					/>
				</div>
			)}
		</Card>
	);
};
